/*
 * Variant custom functions — the storefront API for experiments (A/B tests,
 * landing pages, third-party tools, and the console).
 *
 * Every function is a flat global named variant<Area><Action>:
 *
 *   variantHelp()                   every custom function, grouped
 *   variantHelp('variantCartAdd')   one function in detail, with an example
 *   variantCartRefresh()  variantCartItems()  variantCartClear()  ...
 *
 * Loaded on every page from layout/theme.liquid. Page context comes from the
 * <script id="variant-context"> JSON block Liquid writes next to window.routes.
 *
 * Rules this file keeps, so tests built on it do not break the theme:
 * - Every cart write goes through Shopify's Ajax Cart API and asks, in that
 *   same request, for the sections to re-render: the drawer, the header count
 *   and (on /cart) the cart page — whatever Dawn's own elements list in
 *   getSectionsToRender(). Nothing here knows what a cart line looks like, so a
 *   redesign of the drawer does not touch this file.
 * - Every function that changes the cart returns a Promise of the full cart
 *   (the /cart.js shape). Failures reject with an Error carrying Shopify's own
 *   message.
 * - Scripts that load before this one can queue work:
 *     window.variantQueue = window.variantQueue || [];
 *     variantQueue.push(function () { variantCartRefresh(); });
 *   Queued callbacks run once the functions exist; later pushes run at once.
 * - A new function is ONE row in the FUNCTIONS table near the end: that row
 *   publishes the global and documents it in variantHelp().
 */

(function () {
  if (typeof window.variantHelp === 'function' && window.variantHelp.__variant) return;

  var VERSION = '1.0.0';
  var PREFIX = 'variant:';
  var DEBUG_KEY = 'smk_debug';
  var AB_KEY = 'smk_ab';

  /* Context ------------------------------------------------------------------ */

  var context = {};
  try {
    var contextEl = document.getElementById('variant-context');
    if (contextEl) context = JSON.parse(contextEl.textContent);
  } catch (error) {
    console.warn('[variant] page context could not be read', error);
  }
  if (window.Shopify && window.Shopify.theme) {
    context.theme = {
      id: window.Shopify.theme.id,
      name: window.Shopify.theme.name,
      role: window.Shopify.theme.role,
    };
  }

  /* Utilities ---------------------------------------------------------------- */

  function storage(action, key, value) {
    try {
      if (action === 'get') return window.localStorage.getItem(key);
      if (action === 'set') window.localStorage.setItem(key, value);
      if (action === 'remove') window.localStorage.removeItem(key);
    } catch (error) {
      /* Private mode or blocked storage: behave as if nothing is stored. */
    }
    return null;
  }

  function isDebug() {
    return storage('get', DEBUG_KEY) === '1';
  }

  function log() {
    if (!isDebug()) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('%c[variant]', 'color:#2d2926;background:#f1eb9c;padding:0 4px;border-radius:2px');
    console.log.apply(console, args);
  }

  function route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(PREFIX + name, { detail: detail }));
    log('event', name, detail);
  }

  /* Shopify's Ajax errors come back as 4xx/422 JSON with `description` or
     `message`; anything else becomes a plain HTTP error. */
  function request(url, options) {
    var config = options || {};
    var init = {
      method: config.method || 'GET',
      headers: config.accept === false ? {} : { Accept: 'application/json' },
      credentials: 'same-origin',
      /* A cart read must never come from the HTTP cache: a cached /cart.js
         straight after a write paints the drawer one change behind. */
      cache: 'no-store',
    };
    if (config.body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(config.body);
    }
    log(init.method, url, config.body || '');

    return fetch(url, init).then(function (response) {
      return response.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (error) {
          data = null;
        }
        if (!response.ok || (data && data.status && data.status >= 400)) {
          var message =
            (data && (data.description || data.message)) || 'HTTP ' + response.status + ' for ' + url;
          var failure = new Error(message);
          failure.status = response.status;
          failure.response = data;
          emit('cart:error', { url: url, message: message, response: data });
          throw failure;
        }
        return data;
      });
    });
  }

  function money(cents, format) {
    var pattern = format || (context.shop && context.shop.moneyFormat) || '${{amount}}';
    var token = pattern.match(/\{\{\s*(\w+)\s*\}\}/);
    var value = Number(cents) || 0;

    function group(places, thousands, decimal) {
      var parts = (value / 100).toFixed(places).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
    }

    if (!token) return group(2, ',', '.');
    var formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
    };
    var f = formats[token[1]] || formats.amount;
    return pattern.replace(token[0], group(f[0], f[1], f[2]));
  }

  /* Cart rendering ----------------------------------------------------------- */

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  /* Every section the mounted cart elements want re-rendered, keyed by section
     id — the same walk Dawn's standard-actions-override.js does, but without
     skipping the ones Dawn's own subscribers refresh: this function renders
     them all itself, in one request, and then tells the subscribers to stand
     down (see refresh()). Shopify caps a request at 5 sections; drawer + header
     count + the cart page's own sections come to exactly 5. */
  function cartSections() {
    var sections = new Map();
    var tags = ['cart-drawer', 'cart-notification', 'cart-items'];

    document.querySelectorAll(tags.join(',')).forEach(function (el) {
      var entries;
      try {
        entries = typeof el.getSectionsToRender === 'function' ? el.getSectionsToRender() : [];
      } catch (error) {
        entries = [];
      }

      (entries || []).forEach(function (entry) {
        var id = entry.section || entry.id;
        if (!id || sections.has(id)) return;

        var root = entry.section ? document.getElementById(entry.id) : document;
        if (!root) return;

        var mount = entry.selector
          ? root.querySelector(entry.selector) || (entry.section ? root : null)
          : document.getElementById(entry.id);
        if (!mount) return;

        sections.set(id, { mount: mount, selector: entry.selector || '.shopify-section' });
      });
    });

    return sections;
  }

  function applySections(sections, rendered) {
    if (!rendered) return;
    sections.forEach(function (target, id) {
      var html = rendered[id];
      if (!html) return;
      var source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
      if (source) target.mount.innerHTML = source.innerHTML;
    });
  }

  /* The empty-state classes live on elements OUTSIDE the re-rendered markup, so
     they are set here the way cart.js sets them after its own updates: on the
     drawer, on the cart page's <cart-items> and on its footer. */
  function syncDrawerState(cart) {
    var empty = !cart || cart.item_count === 0;
    document.querySelectorAll('cart-items').forEach(function (items) {
      if (items.tagName !== 'CART-DRAWER-ITEMS') items.classList.toggle('is-empty', empty);
    });
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);

    var el = drawer();
    if (!el) return;
    el.classList.toggle('is-empty', empty);
    /* renderContents() re-binds this after replacing #CartDrawer's children;
       replacing them here drops the old listener the same way. */
    var overlay = el.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof el.close === 'function') overlay.addEventListener('click', el.close.bind(el));
  }

  /* Cart API ----------------------------------------------------------------- */

  function getCart() {
    return request(route('cart_url', '/cart') + '.js');
  }

  function sectionIds(sections) {
    return Array.from(sections.keys()).slice(0, 5);
  }

  /* Paint a cart the server has just rendered, and tell everyone. */
  function settle(cart, sections, rendered, opts) {
    applySections(sections, rendered);
    syncDrawerState(cart);

    /* Dawn's subscribers (price-per-item, quick-add-bulk, quick-order-list,
       recipient-form) need to hear about the change. `source: 'cart-items'`
       is the one value CartItems itself ignores — it would otherwise re-fetch
       and re-render the drawer and cart page a second time, which has just
       been done. */
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }

    if (opts && opts.open) api.cart.open();
    return cart;
  }

  function stripSections(data) {
    var cart = Object.assign({}, data);
    delete cart.sections;
    return cart;
  }

  /* A read has no write to piggyback on, so the markup comes from the Section
     Rendering API on the current path (`?sections=`), fetched alongside the
     cart. `/cart.js?sections=` looks equivalent but ignores the parameter on a
     GET — it returned the cart with no sections and the drawer never changed
     (verified 2026-09-15). The current path, not "/", because the cart page's
     own sections only render on /cart.

     No `Accept: application/json` on this one: on a product URL Shopify treats
     that header as a request for the product itself and answers {"product": …}
     with no sections at all. The sections endpoint returns JSON regardless. */
  function refresh(options) {
    var opts = options || {};
    var sections = cartSections();
    var ids = sectionIds(sections);

    var rendered = ids.length
      ? request(window.location.pathname + '?sections=' + ids.join(','), { accept: false })
      : Promise.resolve(null);

    return Promise.all([getCart(), rendered]).then(function (results) {
      var cart = settle(results[0], sections, results[1], opts);
      emit('cart:refresh', cart);
      return cart;
    });
  }

  function normaliseItems(items, quantity) {
    var list = Array.isArray(items) ? items : [items];
    return list.map(function (item) {
      if (typeof item === 'number' || typeof item === 'string') {
        return { id: Number(item), quantity: quantity || 1 };
      }
      var copy = Object.assign({ quantity: 1 }, item);
      copy.id = Number(copy.id);
      if (copy.selling_plan != null && copy.selling_plan !== '') copy.selling_plan = Number(copy.selling_plan);
      else delete copy.selling_plan;
      return copy;
    });
  }

  /* change()/remove() target: a line number (1-based, < 1000), a line item key
     (string with a colon), or a variant id (any other number).

     The variant id is sent as a STRING: /cart/change.js validates `id` against
     /\A\d+\z/ and rejects a JSON number with a 400 ("expected Integer to be
     a ..."), even though /cart/add.js takes numbers. Verified 2026-09-15. */
  function lineTarget(target) {
    if (typeof target === 'string' && target.indexOf(':') !== -1) return { id: target };
    var n = Number(target);
    if (!n) throw new Error('variant: expected a line number, line key or variant id, got ' + target);
    return n < 1000 ? { line: n } : { id: String(n) };
  }

  /* Writes ask for the sections IN the write — the same request, so the
     markup is rendered from the cart this write produced. Rendering them from
     a separate GET afterwards painted the drawer and header count one change
     behind (seen 2026-09-15: count still 0 after an add). */
  function mutate(url, body, eventName, options) {
    var opts = options || {};
    var sections = cartSections();
    var payload = Object.assign({}, body);
    var ids = sectionIds(sections);
    if (ids.length) {
      payload.sections = ids;
      payload.sections_url = window.location.pathname;
    }

    return request(url, { method: 'POST', body: payload }).then(function (response) {
      var rendered = response && response.sections;
      /* /cart/add.js answers with the added lines, not the cart. */
      var isCart = response && Array.isArray(response.items) && typeof response.item_count === 'number';
      var cartPromise = isCart ? Promise.resolve(stripSections(response)) : getCart();

      return cartPromise.then(function (cart) {
        settle(cart, sections, rendered, opts);
        emit(eventName, { cart: cart, response: stripSections(response || {}) });
        return cart;
      });
    });
  }

  function details(options) {
    var opts = options || {};
    return getCart().then(function (cart) {
      var summary = {
        itemCount: cart.item_count,
        subtotal: money(cart.items_subtotal_price),
        total: money(cart.total_price),
        totalDiscount: money(cart.total_discount),
        currency: cart.currency,
        requiresShipping: cart.requires_shipping,
        note: cart.note,
        attributes: cart.attributes,
        discountCodes: (cart.discount_codes || []).map(function (d) {
          return d.code + (d.applicable ? '' : ' (not applicable)');
        }),
        token: cart.token,
        lines: cart.items.map(function (item, index) {
          return {
            line: index + 1,
            title: item.product_title,
            variant: item.variant_title,
            variantId: item.variant_id,
            quantity: item.quantity,
            price: money(item.final_price),
            lineTotal: money(item.final_line_price),
            sellingPlan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : null,
            key: item.key,
          };
        }),
      };

      if (opts.log !== false) {
        console.log(
          '%cCart%c ' + summary.itemCount + ' items · ' + summary.total,
          'font-weight:bold',
          ''
        );
        if (summary.lines.length) console.table(summary.lines);
        if (Object.keys(summary.attributes || {}).length) console.log('attributes', summary.attributes);
        if (summary.discountCodes.length) console.log('discount codes', summary.discountCodes);
      }
      return summary;
    });
  }

  /* A/B ---------------------------------------------------------------------- */

  function abAll() {
    try {
      return JSON.parse(storage('get', AB_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  /* Cart attributes prefixed with "_" are hidden from the customer at checkout
     but saved on the order, so every order carries the variants its buyer saw. */
  function abSet(name, variant, options) {
    var opts = options || {};
    if (!name) throw new Error('variantAbSet: a test name is required');
    var all = abAll();
    all[name] = String(variant);
    storage('set', AB_KEY, JSON.stringify(all));
    emit('ab:set', { name: name, variant: String(variant) });

    if (opts.cart === false) return Promise.resolve(all);
    var attributes = {};
    attributes['_ab_' + name] = String(variant);
    return request(route('cart_update_url', '/cart/update') + '.js', {
      method: 'POST',
      body: { attributes: attributes },
    }).then(function () {
      return all;
    });
  }

  function abClear(name) {
    var all = abAll();
    var names = name ? [name] : Object.keys(all);
    var attributes = {};
    names.forEach(function (n) {
      delete all[n];
      attributes['_ab_' + n] = '';
    });
    storage('set', AB_KEY, JSON.stringify(all));
    if (!names.length) return Promise.resolve(all);
    return request(route('cart_update_url', '/cart/update') + '.js', {
      method: 'POST',
      body: { attributes: attributes },
    }).then(function () {
      return all;
    });
  }

  /* Events ------------------------------------------------------------------- */

  function on(name, handler) {
    var listener = function (event) {
      handler(event.detail, event);
    };
    document.addEventListener(PREFIX + name, listener);
    return function off() {
      document.removeEventListener(PREFIX + name, listener);
    };
  }

  function once(name, handler) {
    var off = on(name, function (detail, event) {
      off();
      handler(detail, event);
    });
    return off;
  }

  /* Forward every cart change on the site — Dawn's add-to-cart, the drawer's
     steppers, the builder, Skio's upgrade — as variant:cart:update, so a test can
     react to the cart without knowing who changed it. */
  function bridgeDawnEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      emit('cart:update', { source: event && event.source, cart: event && event.cartData });
    });
    subscribe(PUB_SUB_EVENTS.cartError, function (event) {
      emit('cart:error', event);
    });
  }

  /* variant:cart:open / variant:cart:close, read off the drawer's own `active` class so
     it holds however the drawer was opened (header icon, add to cart, API). */
  function watchDrawer() {
    var el = drawer();
    if (!el || typeof MutationObserver === 'undefined') return;
    var wasOpen = el.classList.contains('active');
    new MutationObserver(function () {
      var open = el.classList.contains('active');
      if (open === wasOpen) return;
      wasOpen = open;
      emit(open ? 'cart:open' : 'cart:close', {});
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  /* API ---------------------------------------------------------------------- */

  var api = {
    version: VERSION,
    context: context,

    page: function () {
      return context;
    },

    money: money,

    debug: function (enabled) {
      var on = enabled === undefined ? true : !!enabled;
      if (on) storage('set', DEBUG_KEY, '1');
      else storage('remove', DEBUG_KEY);
      console.log('[variant] debug logging ' + (on ? 'on' : 'off'));
      return on;
    },

    ready: function (callback) {
      if (typeof callback === 'function') callback();
    },

    on: on,
    once: once,

    cart: {
      get: getCart,
      details: details,
      count: function () {
        return getCart().then(function (cart) {
          return cart.item_count;
        });
      },
      refresh: refresh,
      add: function (items, options) {
        var opts = Object.assign({ open: true }, options || {});
        var body = { items: normaliseItems(items, opts.quantity) };
        return mutate(route('cart_add_url', '/cart/add') + '.js', body, 'cart:add', opts);
      },
      change: function (target, quantity, options) {
        var body = Object.assign(lineTarget(target), { quantity: Number(quantity) });
        var opts = options || {};
        if (opts.selling_plan !== undefined) body.selling_plan = opts.selling_plan === null ? null : Number(opts.selling_plan);
        if (opts.properties) body.properties = opts.properties;
        return mutate(route('cart_change_url', '/cart/change') + '.js', body, 'cart:change', opts);
      },
      remove: function (target, options) {
        return mutate(
          route('cart_change_url', '/cart/change') + '.js',
          Object.assign(lineTarget(target), { quantity: 0 }),
          'cart:remove',
          options
        );
      },
      clear: function (options) {
        return mutate(route('cart_url', '/cart') + '/clear.js', {}, 'cart:clear', options);
      },
      update: function (payload, options) {
        return mutate(route('cart_update_url', '/cart/update') + '.js', payload || {}, 'cart:change', options);
      },
      setAttributes: function (attributes, options) {
        return api.cart.update({ attributes: attributes || {} }, options);
      },
      setNote: function (note, options) {
        return api.cart.update({ note: note == null ? '' : String(note) }, options);
      },
      applyDiscount: function (codes, options) {
        var list = Array.isArray(codes) ? codes : [codes];
        return api.cart.update({ discount: list.filter(Boolean).join(',') }, options);
      },
      removeDiscounts: function (options) {
        return api.cart.update({ discount: '' }, options);
      },
      open: function () {
        var el = drawer();
        if (el && typeof el.open === 'function') {
          el.open();
          return true;
        }
        window.location.href = route('cart_url', '/cart');
        return false;
      },
      close: function () {
        var el = drawer();
        if (el && typeof el.close === 'function') el.close();
      },
      toggle: function () {
        var el = drawer();
        if (el && el.classList.contains('active')) api.cart.close();
        else api.cart.open();
      },
      isOpen: function () {
        var el = drawer();
        return !!(el && el.classList.contains('active'));
      },
    },

    product: {
      get: function (handle) {
        var h = handle || (context.product && context.product.handle);
        if (!h) return Promise.reject(new Error('variantProductGet: no handle, and this is not a product page'));
        return request('/products/' + encodeURIComponent(h) + '.js');
      },
      current: function () {
        return context.product || null;
      },
    },

    ab: {
      get: function (name) {
        var value = abAll()[name];
        return value === undefined ? null : value;
      },
      set: abSet,
      all: abAll,
      clear: abClear,
    },
  };

  api.cart.empty = api.cart.clear;

  api.cart.items = function (options) {
    var opts = options || {};
    return details({ log: false }).then(function (summary) {
      if (opts.log !== false) {
        if (summary.lines.length) console.table(summary.lines);
        else console.log('[variant] the cart is empty');
      }
      return summary.lines;
    });
  };

  /* Public functions ---------------------------------------------------------

     Every custom function is a flat global named `variant<Area><Action>`
     (variantCartRefresh, variantCartItems, ...), the naming convention for all
     Variant custom code on this store. This table is the single source for
     both the globals and variantHelp(): add a row here and the function is
     published and documented at once.

     [name, function, group, signature, what it does, example] */
  var FUNCTIONS = [
    ['variantHelp', null, 'General', "variantHelp(name?)", 'List every custom function, or show one in detail with an example.', "variantHelp('variantCartAdd')"],
    ['variantVersion', function () { return VERSION; }, 'General', 'variantVersion() → string', 'Version of this API.', 'variantVersion()'],
    ['variantPage', api.page, 'General', 'variantPage() → object', 'Page context: template, page type, path, product, collection, customer.loggedIn, locale, country, currency, theme.', 'variantPage().template'],
    ['variantMoney', money, 'General', 'variantMoney(cents, format?) → string', "Format cents in the shop's money format.", 'variantMoney(2999) // "$29.99"'],
    ['variantDebug', api.debug, 'General', 'variantDebug(on = true)', 'Log every request and event these functions make (remembered in this browser).', 'variantDebug(false)'],
    ['variantOn', on, 'General', 'variantOn(event, handler) → off()', 'Listen to an event. Also dispatched on document as "variant:<event>". Events: cart:update, cart:refresh, cart:add, cart:change, cart:remove, cart:clear, cart:error, cart:open, cart:close, ab:set, ready.', "const off = variantOn('cart:update', ({ source, cart }) => console.log(source, cart))"],
    ['variantOnce', once, 'General', 'variantOnce(event, handler) → off()', 'Like variantOn, removed after the first call.', "variantOnce('cart:open', () => console.log('opened'))"],
    ['variantReady', api.ready, 'General', 'variantReady(fn)', 'Run fn now. From a script that may load BEFORE these functions exist, queue instead: (window.variantQueue = window.variantQueue || []).push(fn).', 'variantQueue.push(() => variantCartRefresh())'],

    ['variantCartGet', api.cart.get, 'Cart', 'variantCartGet() → Promise<cart>', 'The raw cart (/cart.js).', 'await variantCartGet()'],
    ['variantCartItems', api.cart.items, 'Cart', 'variantCartItems({ log = true }) → Promise<lines[]>', 'Cart lines: line number, title, variant, variant id, quantity, price, line total, selling plan, line key. Prints a table unless log:false.', 'await variantCartItems()'],
    ['variantCartDetails', api.cart.details, 'Cart', 'variantCartDetails({ log = true }) → Promise<summary>', 'Whole-cart summary: lines, item count, subtotal, total, discount, attributes, note, discount codes, token.', 'await variantCartDetails()'],
    ['variantCartCount', api.cart.count, 'Cart', 'variantCartCount() → Promise<number>', 'Total item quantity in the cart.', 'await variantCartCount()'],
    ['variantCartRefresh', api.cart.refresh, 'Cart', 'variantCartRefresh({ open = false }) → Promise<cart>', 'Re-render the cart drawer, header count and /cart page from the server. Use after the cart was changed from outside the theme.', 'await variantCartRefresh({ open: true })'],
    ['variantCartAdd', api.cart.add, 'Cart', 'variantCartAdd(items, { open = true, quantity }) → Promise<cart>', 'Add one or many variants. items: a variant id, { id, quantity, selling_plan, properties }, or an array of either. Opens the drawer unless open:false.', 'await variantCartAdd([{ id: 50442750853368, quantity: 1, selling_plan: 6191644920 }])'],
    ['variantCartChange', api.cart.change, 'Cart', 'variantCartChange(target, quantity, { selling_plan, properties, open }) → Promise<cart>', "Set a line's quantity. target: line number (1-based), line key, or variant id. selling_plan: a plan id to switch, null to make it one-time.", 'await variantCartChange(1, 3)'],
    ['variantCartRemove', api.cart.remove, 'Cart', 'variantCartRemove(target) → Promise<cart>', 'Remove a line (line number, line key or variant id).', 'await variantCartRemove(2)'],
    ['variantCartClear', api.cart.clear, 'Cart', 'variantCartClear() → Promise<cart>', 'Empty the cart (attributes and note are kept).', 'await variantCartClear()'],
    ['variantCartEmpty', api.cart.clear, 'Cart', 'variantCartEmpty() → Promise<cart>', 'Same as variantCartClear.', 'await variantCartEmpty()'],
    ['variantCartUpdate', api.cart.update, 'Cart', 'variantCartUpdate(payload) → Promise<cart>', 'Raw /cart/update.js: { updates, attributes, note, discount }.', 'await variantCartUpdate({ updates: { 50442750853368: 2 } })'],
    ['variantCartSetAttributes', api.cart.setAttributes, 'Cart', 'variantCartSetAttributes(object) → Promise<cart>', 'Merge cart attributes. Keys starting with "_" are hidden at checkout but kept on the order. Set a value to "" to remove it.', "await variantCartSetAttributes({ _landing: 'lp-10-reasons' })"],
    ['variantCartSetNote', api.cart.setNote, 'Cart', 'variantCartSetNote(text) → Promise<cart>', 'Replace the cart note.', "await variantCartSetNote('Gift wrap please')"],
    ['variantCartApplyDiscount', api.cart.applyDiscount, 'Cart', 'variantCartApplyDiscount(code | codes[]) → Promise<cart>', 'Apply discount code(s). Check cart.discount_codes[].applicable to see whether it took.', "await variantCartApplyDiscount('WELCOME10')"],
    ['variantCartRemoveDiscounts', api.cart.removeDiscounts, 'Cart', 'variantCartRemoveDiscounts() → Promise<cart>', 'Remove every discount code from the cart.', 'await variantCartRemoveDiscounts()'],
    ['variantCartOpen', api.cart.open, 'Cart', 'variantCartOpen()', 'Open the cart drawer (goes to /cart when there is no drawer).', 'variantCartOpen()'],
    ['variantCartClose', api.cart.close, 'Cart', 'variantCartClose()', 'Close the cart drawer.', 'variantCartClose()'],
    ['variantCartToggle', api.cart.toggle, 'Cart', 'variantCartToggle()', 'Open or close the cart drawer.', 'variantCartToggle()'],
    ['variantCartIsOpen', api.cart.isOpen, 'Cart', 'variantCartIsOpen() → boolean', 'Whether the cart drawer is open.', 'variantCartIsOpen()'],

    ['variantProductGet', api.product.get, 'Product', 'variantProductGet(handle?) → Promise<product>', "A product's /products/<handle>.js (variants, prices, selling plans). Defaults to the current product.", "await variantProductGet('creatine')"],
    ['variantProductCurrent', api.product.current, 'Product', 'variantProductCurrent() → object|null', "This page's product context (id, handle, selected variant, ...), or null off a product page.", 'variantProductCurrent()'],

    ['variantAbSet', api.ab.set, 'A/B tests', 'variantAbSet(test, variant, { cart = true }) → Promise', 'Record the variant this visitor sees (this browser) and tag the cart with the hidden attribute "_ab_<test>", so it lands on the order.', "await variantAbSet('pdp-builder', 'B')"],
    ['variantAbGet', api.ab.get, 'A/B tests', 'variantAbGet(test) → string|null', 'The recorded variant for a test.', "variantAbGet('pdp-builder')"],
    ['variantAbAll', api.ab.all, 'A/B tests', 'variantAbAll() → object', 'Every recorded test and its variant.', 'variantAbAll()'],
    ['variantAbClear', api.ab.clear, 'A/B tests', 'variantAbClear(test?) → Promise', 'Forget one test (or all of them) and remove its cart attribute.', "await variantAbClear('pdp-builder')"],
  ];

  function variantHelp(name) {
    if (name) {
      var key = String(name).replace(/^window\./, '').replace(/\(.*$/, '').trim();
      var row = FUNCTIONS.find(function (f) {
        return f[0].toLowerCase() === key.toLowerCase();
      });
      if (!row) {
        console.warn('[variant] no function "' + name + '". Run variantHelp() for the list.');
        return;
      }
      console.log('%c' + row[3], 'font-weight:bold;font-family:monospace');
      console.log(row[4]);
      console.log('%cExample:%c ' + row[5], 'font-weight:bold', 'font-family:monospace');
      return;
    }

    console.log(
      '%cVariant custom functions ' + VERSION + '%c  ' + FUNCTIONS.length + " available — variantHelp('variantCartAdd') for details and an example",
      'color:#2d2926;background:#f1eb9c;font-weight:bold;padding:2px 6px;border-radius:3px',
      'color:inherit'
    );
    var groups = {};
    FUNCTIONS.forEach(function (f) {
      (groups[f[2]] = groups[f[2]] || []).push({ function: f[3], does: f[4] });
    });
    Object.keys(groups).forEach(function (group) {
      console.log('%c' + group, 'font-weight:bold;text-transform:uppercase;margin-top:6px');
      console.table(groups[group]);
    });
  }

  /* Publish ------------------------------------------------------------------ */

  FUNCTIONS[0][1] = variantHelp;

  var published = [];
  FUNCTIONS.forEach(function (f) {
    var name = f[0];
    if (window[name] !== undefined && !(window[name] && window[name].__variant)) {
      console.warn('[variant] window.' + name + ' is already defined by something else; not overwritten.');
      return;
    }
    var fn = f[1];
    fn.__variant = true;
    window[name] = fn;
    published.push(name);
  });

  /* The list itself, for tooling: variantFunctions() → ['variantHelp', ...]. */
  window.variantFunctions = function () {
    return published.slice();
  };
  window.variantFunctions.__variant = true;

  /* Boot --------------------------------------------------------------------- */

  function boot() {
    bridgeDawnEvents();
    watchDrawer();

    var queued = Array.isArray(window.variantQueue) ? window.variantQueue : [];
    var runner = {
      push: function () {
        Array.prototype.forEach.call(arguments, function (fn) {
          if (typeof fn !== 'function') return;
          try {
            fn();
          } catch (error) {
            console.error('[variant] queued callback failed', error);
          }
        });
        return 0;
      },
    };
    window.variantQueue = runner;
    runner.push.apply(runner, queued);

    emit('ready', { version: VERSION, functions: published.slice() });
    log('ready', VERSION, context);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
